/**
 * Auto-fit text sizing for 1080×1080 share cards.
 *
 * The problem: cards use fixed fontSize (e.g. 124px). Long titles like
 * "Bulgarian Split Squat" or "Barbell Romanian Deadlift" overflow the
 * card horizontally when rendered to PNG for social sharing.
 *
 * The solution: character-count-based dynamic sizing. Computes an ideal
 * fontSize that fits the given text inside `maxWidth`, clamped between
 * `minSize` and `maxSize`.
 *
 * We use character-count rather than actual text measurement because
 * these share cards are rendered off-screen and immediately converted to
 * PNG via html-to-image — there's no browser paint step in between where
 * a getBoundingClientRect would be reliable. Character-count is
 * deterministic and produces a stable result across every render.
 *
 * The 0.55 coefficient is empirically tuned for Barlow Condensed uppercase
 * (the display font used in every card). Regular-case text lands slightly
 * narrower; if a card uses mixed case, you can pass `avgCharRatio: 0.50`.
 */
export function fitTextSize(text, {
    maxWidth,
    minSize = 40,
    maxSize = 124,
    avgCharRatio = 0.55,
} = {}) {
    const s = String(text || "").trim();
    if (!s || !maxWidth) return maxSize;
    const perChar = maxWidth / s.length;
    const idealSize = Math.floor(perChar / avgCharRatio);
    return Math.max(minSize, Math.min(maxSize, idealSize));
}

/**
 * For strings that are still too long even at minSize, split into two lines
 * at the nearest space. Returns { lines: [string, string], size: number }.
 * If the string fits on one line at maxSize, returns { lines: [s], size: maxSize }.
 *
 * Rule of thumb: prefer a single-line larger font whenever the string is
 * short enough. Only wrap to two lines when the single-line font would drop
 * below `wrapThreshold` (default 70px — cards look weak below that).
 */
export function fitTitleWithWrap(text, {
    maxWidth,
    maxSize = 124,
    minSize = 60,
    wrapThreshold = 70,
    avgCharRatio = 0.55,
} = {}) {
    const s = String(text || "").trim();
    if (!s || !maxWidth) return { lines: [s], size: maxSize };
    const singleLine = fitTextSize(s, { maxWidth, minSize, maxSize, avgCharRatio });
    if (singleLine >= wrapThreshold || !s.includes(" ")) {
        return { lines: [s], size: singleLine };
    }
    // Wrap: split near the middle at the closest whitespace so both halves
    // are roughly balanced (a title like "BARBELL ROMANIAN DEADLIFT" splits
    // to "BARBELL ROMANIAN" / "DEADLIFT" rather than "BARBELL" / "ROMANIAN
    // DEADLIFT" — heavier line on top reads better in tight card layouts).
    const words = s.split(/\s+/);
    let bestSplit = 1;
    let bestDelta = Infinity;
    for (let i = 1; i < words.length; i++) {
        const a = words.slice(0, i).join(" ");
        const b = words.slice(i).join(" ");
        const delta = Math.abs(a.length - b.length);
        if (delta < bestDelta) { bestDelta = delta; bestSplit = i; }
    }
    const line1 = words.slice(0, bestSplit).join(" ");
    const line2 = words.slice(bestSplit).join(" ");
    // Size the wrapped block by the LONGER of the two lines so both fit.
    const longest = line1.length >= line2.length ? line1 : line2;
    const wrappedSize = fitTextSize(longest, { maxWidth, minSize, maxSize, avgCharRatio });
    return { lines: [line1, line2], size: wrappedSize };
}
